import config from '@/config'
import useProfileStore, { constantsKey } from '@/stores/profile.store'
import pinia from '@/stores/pinia'

/**
 * Gets the constants from the profile store
 * @returns {Object} - Presets constants from the profile store
 */
function getConstants() {
  const profileStore = useProfileStore(pinia)
  const constants = profileStore[constantsKey]
  return constants ? constants.presets : null
}

const iconsLocation = config.assetsLocations.icons

/**
 * Returns true if the provided preset is a color (wheel) preset
 * @param {Preset} preset - Preset to test if a color (wheel) preset
 * @returns {Boolean} Whether the preset is a color (wheel) preset or not
 */
export function isColor(preset) {
  const presetConstants = getConstants()
  return (
    preset
    && presetConstants
    && presetConstants.groups.colors.includes(preset.preset_type)
  )
}

/**
 * Returns true if the provided preset is a GOBO preset
 * @param {Preset} preset - Preset to test if GOBO preset
 * @returns {Boolean} Whether the preset is a GOBO preset or not
 */
export function isGobo(preset) {
  const presetConstants = getConstants()
  return (
    preset
    && presetConstants
    && preset.preset_type === presetConstants.types.SSL_PR_GOBO
  )
}

const UNDEF_ICON = 'Undef_Undef.png'

/**
 * Get the path of the icon of a preset, or it's colors if applicable
 * @param {Preset} preset - Preset for which to get the icon
 * @returns {String} Path of the icon
 */
export function getIcon(preset) {
  // if the icon is falsy, use the UNDEF icon
  let iconName = preset?._icon || UNDEF_ICON
  // if the icon is 'noIcon', use the UNDEF icon
  iconName = iconName.toLowerCase() === 'noicon' ? UNDEF_ICON : iconName
  if (iconName === UNDEF_ICON && isColor(preset)) {
    return preset.colors
  }
  if (isGobo(preset)) {
    // If this is a gobo preset, show the gobo asset
    return `${iconsLocation.gobos}/${iconName}`
  }
  // If this is any other kind of preset, show the preset asset
  return `${iconsLocation.presets}/${iconName}`
}

export const unitsTranslations = {
  Generic: {
    param_min: 'preset.param_min',
    param_max: 'preset.param_max',
  },
  Percentage: {
    param_min: 'params.percentMin',
    param_max: 'params.percentMax',
  },
  FixedPercentage: {
    param_min: 'params.percentFix',
  },
  Angle: {
    param_min: 'params.angleMin',
    param_max: 'params.angleMax',
  },
  FixedAngle: {
    param_min: 'params.angleFix',
  },
  Filter: {
    param_min: 'params.filterMin',
    param_max: 'params.filterMax',
  },
  FixedFilter: {
    param_min: 'params.filterFix',
  },
  Frequency: {
    param_min: 'params.frequencyMin',
    param_max: 'params.frequencyMax',
  },
  FixedFrequency: {
    param_min: 'params.frequencyFix',
  },
}
